namespace Core {
    'use strict';
    type TripState = Core.Trip.TripState;
    type Destination = Core.Trip.Destination;
    type HotelsRequest = Core.Trip.HotelsRequest;
    type FlightsRequest = Core.Trip.FlightsRequest;
    type MultiFlightsRequest = Core.Trip.MultiFlightsRequest;
    type TransfersRequest = Core.Trip.TransfersRequest;
    type MarhabaRequest = Core.Trip.MarhabaRequest;
    type InsuranceRequest = Core.Trip.InsuranceRequest;
    type CarHireRequest = Core.Trip.CarHireRequest;
    type ParkingRequest = Core.Trip.ParkingRequest;
    type AttractionRequest = Core.Trip.AttractionRequest;
    type Flight = Core.Flights.Flight;
    type Airport = Core.Trip.Airport;
    type Occupancy = Core.Trip.Occupancy;
    type TripFromQuoteRequest = Core.Trip.TripFromQuoteRequest;
    type TripFromQuoteResponse = Core.Trip.TripFromQuoteResponse;
    type TripFromDealResponse = Core.Trip.TripFromDealResponse;

    angular
        .module('TR')
        .service('tripService', tripService);

    export interface TripService {
        tripTypes: any; //TODO check that this actually works
        persist(tripId: string, trip: TripState) : ng.IPromise<TripState>;
        getTrip(tripId: string): ng.IPromise<TripState|void>;
        getTripDestination(tripId: string): ng.IPromise<Destination|null>;
        getCurrency(tripId: string): ng.IPromise<string|void>;
        isKidsFree(tripId: string): ng.IPromise<boolean>;
        recordHotelSearch(tripId: string, search: HotelsRequest): ng.IPromise<TripState>;
        recordFlightSearch(tripId: string, search: FlightsRequest): ng.IPromise<TripState>;
        recordMultiFlightSearch(tripId: string, search: MultiFlightsRequest): ng.IPromise<TripState>;
        recordTransferSearch(tripId: string, search: TransfersRequest): ng.IPromise<TripState>;
        recordMarhabaSearch(tripId: string, search: MarhabaRequest): ng.IPromise<TripState>;
        recordInsuranceSearch(tripId: string, search: InsuranceRequest): ng.IPromise<TripState>;
        recordCarHireSearch(tripId: string, search: CarHireRequest): ng.IPromise<TripState>;
        recordParkingSearch(tripId: string, search: ParkingRequest): ng.IPromise<TripState>;
        recordAttractionSearch(tripId: string, search: AttractionRequest): ng.IPromise<TripState>;
        clearOptionalExtras(tripId: string): ng.IPromise<TripState>;
        changeTripType(tripId: string, typeId: number): ng.IPromise<TripState>;
        setBookingId(tripId: string, bookingId: string): ng.IPromise<TripState>;
        setBasketId(tripId: string, basketId: string): ng.IPromise<TripState>;
        setQuoteId(tripId: string, quoteId: string): ng.IPromise<TripState>;
        setKidsFree(tripId: string, isKidsFree: boolean): ng.IPromise<TripState>;
        setDirectToHotel(tripId: string, direct: boolean): ng.IPromise<TripState>;
        setSelectedFlight(tripId: string, flight: Flight, opts): ng.IPromise<TripState>;
        restoreSearchUnit(tripId: string): ng.IPromise<TripState|void>;
        getHotelsRequest(tripId: string):ng.IPromise<HotelsRequest|undefined>;
        getHotelsRequestCheckInDate(tripId: string, serialised: boolean): ng.IPromise<Date|string|null>;
        getHotelsRequestCheckOutDate(tripId: string, serialised: boolean): ng.IPromise<Date|string|null>;
        getFlightsRequestDepartureDate(tripId: string, serialised: boolean): ng.IPromise<Date|string|null>;
        getSelectedDepartureAirport(tripId: string): ng.IPromise<Airport|null>;
        getDepartureAirports(tripId: string): ng.IPromise<any>;
        getParkingAirport(tripId: string): ng.IPromise<Airport>;
        getSelectedDestinationAirport(tripId: string): ng.IPromise<Airport|null>;
        getDestinationAirports(tripId: string): ng.IPromise<any>;
        getFlightCabin(tripId: string): ng.IPromise<number>;
        getParkingDropOffDate(tripId: string, serialised: boolean): ng.IPromise<Date|string|null>;
        getParkingPickUpDate(tripId: string, serialised: boolean): ng.IPromise<Date|string|null>;
        getTripNumberOfNights(mode: number, tripId: string): ng.IPromise<number|null|void>;
        getDurationSummary(mode: number, tripId: string): ng.IPromise<string>;
        isOneWayFlightEnabled(tripId: string): ng.IPromise<boolean>;
        getTripOccupancy(tripId: string): ng.IPromise<Occupancy[]|null>;
        getTripReducedOccupancy(tripId: string): ng.IPromise<Occupancy>;
        getTransferDestinationAirport(tripId: string): ng.IPromise<Airport|null>;
        getTransfersRequestSummary(tripId: string): ng.IPromise<any>;
        getParkingRequestSummary(tripId: string): ng.IPromise<any>;
        getHotelsRequestNumberOfNights(tripId: string): ng.IPromise<number>;
        getTripFromQuote(quote): ng.IPromise<TripFromQuoteResponse>;
        supportsTransfers(trip: TripState): boolean;
        getTripIdForDeal(deal: any, originPath: string): ng.IPromise<string>;
        getTripIdForHolidayDeal(deal, occupancy): ng.IPromise<string>;
        getFlightsRequestReturnDate(tripId: string, serialised:boolean): ng.IPromise<Date|string|null>;
        usePackageRates(tripId?: string): ng.IPromise<boolean>;
        getFlightsRequest(tripId: string): ng.IPromise<FlightsRequest>;
        getTransfersRequest(tripId: string): ng.IPromise<TransfersRequest>;
        getInsuranceRequestSummary(tripId: string): ng.IPromise<any>;
    }

    type TripRequest =
        HotelsRequest
            | FlightsRequest
            | MultiFlightsRequest
            | TransfersRequest
            | MarhabaRequest
            | InsuranceRequest
            | CarHireRequest
            | ParkingRequest;

    function tripService(loggingService : ILoggingService,
                         $q: ng.IQService,
                         dateUtil,
                         $http: IHttpService,
                         searchUnitService: SearchUnitService,
                         broker: Broker,
                         occupancyService: OccupancyService,
                         $routeParams,
                         $location,
                         destinationService) : TripService {
        const url: string = '/api2/tripstate/';
        const promiseCache: {[id:string]:ng.IPromise<TripState>} = {};
        const tripTypes = {
            None: 0,
            Flight: 1,
            Hotel: 2,
            Transfer: 3,
            Holiday: 4,
            Marhaba: 5,
            Parking: 6,
            Attractions: 7,
            DynamicPackage: 8,
            Insurance: 9,
            MultiDestHoliday: 10
        };
        const defaultOpts = {
            bags: 0
        };
        let tripQueue: ng.IPromise<TripState|void> = $q.when();

        const service: TripService = {
            tripTypes,
            persist,
            getTrip,
            getTripDestination,
            recordHotelSearch,
            recordFlightSearch,
            recordMultiFlightSearch,
            recordTransferSearch,
            recordMarhabaSearch,
            recordInsuranceSearch,
            recordCarHireSearch,
            recordParkingSearch,
            recordAttractionSearch,
            clearOptionalExtras,
            changeTripType,
            setBookingId,
            setBasketId,
            setQuoteId,
            setKidsFree,
            setSelectedFlight,
            supportsTransfers,
            setDirectToHotel,
            restoreSearchUnit,
            getCurrency,
            isKidsFree,
            getTripIdForDeal,
            getTripIdForHolidayDeal,
            getHotelsRequest,
            getHotelsRequestCheckInDate,
            getHotelsRequestCheckOutDate,
            getHotelsRequestNumberOfNights,
            getFlightsRequest,
            getFlightsRequestDepartureDate,
            getFlightsRequestReturnDate,
            getParkingDropOffDate,
            getParkingPickUpDate,
            getSelectedDepartureAirport,
            getDepartureAirports,
            getSelectedDestinationAirport,
            getDestinationAirports,
            getFlightCabin,
            getParkingAirport,
            isOneWayFlightEnabled,
            getTripNumberOfNights,
            getDurationSummary,
            getTripOccupancy,
            getTripReducedOccupancy,
            getTransfersRequestSummary,
            getTransferDestinationAirport,
            getParkingRequestSummary,
            getTripFromQuote,
            usePackageRates,
            getInsuranceRequestSummary,
            getTransfersRequest
        };

        function _updateTrip(tripId: string, fn: (TripState) => TripState ): ng.IPromise<TripState> {
            return service.getTrip(tripId).then(trip => {
                service.persist(tripId, fn(trip));
                return $q.when(trip);
            });
        }


        function _recordSearch(tripId: string,
                               search: TripRequest,
                               name: string) {
            return _updateTrip(tripId, trip => {
                trip[name] = search;
                return trip;
            });
        }

        function persist(tripId: string, trip: TripState) : ng.IPromise<TripState> {
            promiseCache[tripId] = $http.post(`${url}updatetripstate`, {
                TripId: tripId,
                TripState: trip
            }).then(() => {
                const tripCopy = angular.copy<TripState>(trip);
                broker.publish('tripUpdated', tripCopy);
                return tripCopy;
            });
            return promiseCache[tripId];
        }

        function supportsTransfers(trip: TripState): boolean {
            return trip.TripType !== Core.Trip.TripType.Flight;
        }

        function getTrip(tripId: string): ng.IPromise<TripState|void> {
            if (!tripId) {
                return $q.when();
            }

            if (promiseCache[tripId] === undefined) {
                const validateUser = Boolean(TRPCONFIG.Domain.ValidateUserForTripstate);
                promiseCache[tripId] = $http.get<TripState>(
                    `${url}gettripstate?tripId=${tripId}&validateUser=${validateUser}`
                    ).then(res => {
                        if(res.data.TripId !== $routeParams.tripId) {
                            //load new trip and basket clone
                            $location.path($location.path().replace($routeParams.tripId, res.data.TripId));
                        }
                        return _enrichTripDestination(res.data);
                });
            }

            tripQueue = tripQueue.then<TripState>(() => {
                return promiseCache[tripId].then(trip => {
                    return angular.copy(trip);
                });
            });

            return tripQueue;

            function _enrichTripDestination(trip: TripState): ng.IPromise<TripState> {
                const detailsPromise: ng.IPromise<any>[] = [];

                detailsPromise.push(trip.FlightsRequest && trip.FlightsRequest.Destination
                        ? destinationService.getDetails(trip.FlightsRequest.Destination)
                        : $q.when());

                detailsPromise.push(trip.HotelsRequest
                    && trip.HotelsRequest.Destination
                        ? destinationService.getDetails(trip.HotelsRequest.Destination)
                        : $q.when());

                return $q.all(detailsPromise).then(([flightDest, hotelDest]) => {
                    if (flightDest && trip.FlightsRequest) {
                        trip.FlightsRequest.Destination = flightDest;
                    }
                    if (hotelDest && trip.HotelsRequest) {
                        trip.HotelsRequest.Destination = hotelDest;
                    }
                    return trip;
                }, () => {
                    loggingService.logInfo(`Get Details is failing for ${trip.TripId}`);
                    return trip;
                });
            }
        }

        function getTripDestination(tripId: string): ng.IPromise<Destination|null> {
            return getTrip(tripId).then<Destination|null>(trip => {
                if (trip) {
                    const flightDest = trip.FlightsRequest && trip.FlightsRequest.Destination;
                    const hotelsDest = trip.HotelsRequest && trip.HotelsRequest.Destination;
                    return flightDest || hotelsDest || null;
                }
                return null;
            }, () => { tripQueue = $q.when(); });
        }

        function getCurrency(tripId: string): ng.IPromise<string|void> {
            if (tripId == null) {
                return $q.when();
            }
            return getTrip(tripId).then<string|void>(trip => {
                if (trip.HotelsRequest != null) {
                    return trip.HotelsRequest.CurrencyCode;
                }
            });
        }

        function isKidsFree(tripId: string): ng.IPromise<boolean> {
            if (tripId == null) {
                return $q.when(false);
            }
            return getTrip(tripId).then(trip => {
                return !!trip.IsKidsFree;
            });
        }

        function getTripIdForHolidayDeal(deal, occupancy): ng.IPromise<string> {
            let tripId;
            const params = _.assignIn({}, deal, {
                DomainId: TRPCONFIG.Domain.DomainId,
                Occupancy: _.first(occupancy)
            });
            return $http.post<TripFromDealResponse>(
                `${url}createtripfromholidaydeal`, params, {ignoreErrors: true})
                .then(
                    res => tripId = res.data.TripId,
                    res => $q.reject(res)
                )
                .then(() => _updateTrip(tripId, trip => {
                    trip.HotelsRequest.DirectToHotel = false;
                    return trip;
                }))
                .then(() => tripId);
        }

        function getTripIdForDeal(deal: any, originPath: string): ng.IPromise<string> {
            return $http.post<TripFromDealResponse>(`${url}createtripfromdeal`, {
                AvailabilitySearchId: deal.AvailabilitySearchId,
                EstablishmentId: deal.EstablishmentId,
                Deal: deal,
                OriginPath: originPath,
                DomainId: TRPCONFIG.Domain.DomainId
            }, {ignoreErrors: true}).then(res => res.data.TripId, res => {
                if (res.status === 404) {
                    loggingService.logWarn(
                        `Unable to create a trip for deal ${deal.Id} with availabilityId ${deal.AvailabilitySearchId}`);
                }
                return $q.reject(res);
            });
        }

        function recordHotelSearch(tripId: string, search: HotelsRequest): ng.IPromise<TripState> {
            search.DirectToHotel = true;
            return _recordSearch(tripId, search, 'HotelsRequest');
        }

        function recordFlightSearch(tripId: string, search: FlightsRequest): ng.IPromise<TripState> {
            return _recordSearch(tripId, search, 'FlightsRequest');
        }

        function recordMultiFlightSearch(tripId: string, search: MultiFlightsRequest): ng.IPromise<TripState> {
            return _recordSearch(tripId, search, 'MultiFlightsRequest');
        }

        function recordTransferSearch(tripId: string, search: TransfersRequest): ng.IPromise<TripState> {
            return _recordSearch(tripId, search, 'TransfersRequest');
        }

        function recordMarhabaSearch(tripId: string, search: MarhabaRequest): ng.IPromise<TripState> {
            return _recordSearch(tripId, search, 'MarhabaRequest');
        }

        function recordInsuranceSearch(tripId: string, search: InsuranceRequest): ng.IPromise<TripState> {
            return _recordSearch(tripId, search, 'InsuranceRequest');
        }

        function recordCarHireSearch(tripId: string, search: CarHireRequest): ng.IPromise<TripState> {
            return _recordSearch(tripId, search, 'CarHireRequest');
        }

        function recordParkingSearch(tripId: string, search: ParkingRequest): ng.IPromise<TripState> {
            return _recordSearch(tripId, search, 'ParkingRequest');
        }

        function recordAttractionSearch(tripId: string, search: AttractionRequest): ng.IPromise<TripState> {
            return _recordSearch(tripId, search, 'AttractionRequest');
        }

        function clearOptionalExtras(tripId: string): ng.IPromise<TripState> {
            return _updateTrip(tripId, trip => {
                trip.TransfersRequest = undefined;
                trip.InsuranceRequest = undefined;
                trip.CarHireRequest = undefined;
                trip.ParkingRequest = undefined;
                trip.AttractionRequest = undefined;

                //TODO - set other optional extra requests to undefined
                return trip;
            });
        }

        function changeTripType(tripId: string, typeId: number): ng.IPromise<TripState> {
            return _updateTrip(tripId, trip => {
                trip.TripType = typeId;
                return trip;
            });
        }

        function setBookingId(tripId: string, bookingId: string): ng.IPromise<TripState> {
            return _updateTrip(tripId, trip => {
                trip.BookingId = bookingId;
                return trip;
            });
        }

        function setKidsFree(tripId: string, isKidsFree: boolean): ng.IPromise<TripState> {
            return _updateTrip(tripId, (trip:TripState) => {
                trip.IsKidsFree = isKidsFree;
                return trip;
            });
        }

        function setBasketId(tripId: string, basketId: string): ng.IPromise<TripState> {
            return _updateTrip(tripId, trip => {
                trip.BasketId = basketId;
                return trip;
            });
        }

        function setQuoteId(tripId: string, quoteId: string): ng.IPromise<TripState> {
            return _updateTrip(tripId, trip => {
                trip.QuoteId = quoteId;
                return trip;
            });
        }

        function setDirectToHotel(tripId: string, direct: boolean): ng.IPromise<TripState> {
            return service.getTrip(tripId).then(function (trip) {
                if (trip.HotelsRequest && trip.HotelsRequest.DirectToHotel !== direct) {
                    return _updateTrip(tripId, function (trip) {
                        if (trip.HotelsRequest) {
                            trip.HotelsRequest.DirectToHotel = direct;
                        }
                        return trip;
                    });
                }
                return trip;
            });
        }

        function setSelectedFlight(tripId: string, flight: Flight, opts): ng.IPromise<TripState> {
            opts = opts || defaultOpts;
            return _updateTrip(tripId, trip => {
                if (trip.FlightsRequest) {
                    trip.FlightsRequest.SelectedFlight = {
                        FlightId: flight.FlightId,
                        DetailsId: flight.DetailsId,
                        LeadAdultPrice: flight.LeadAdultPrice,
                        LeadAdultTax: flight.LeadAdultTax,
                        LeadBaggagePrice: flight.LeadBaggagePrice,
                        AllBagsOrNone: flight.AllBagsOrNone,
                        BaggageCount: opts.bags
                    };
                }
                return trip;
            });
        }

        function restoreSearchUnit(tripId: string): ng.IPromise<TripState|void> {
            if (!tripId) {
                return $q.when();
            }
            return service.getTrip(tripId).then<TripState|void>(trip =>
                $q.all([
                    searchUnitService.restoreFlightSearch(trip),
                    searchUnitService.restoreMultiFlightSearch(trip),
                    searchUnitService.restoreTransferSearch(trip),
                    searchUnitService.restoreMarhabaSearch(trip),
                    searchUnitService.restoreHotelSearch(trip),
                    searchUnitService.restoreParkingSearch(trip),
                    searchUnitService.restoreInsuranceSearch(trip)
                ])
                    .then(function () {
                        return trip;
                    })
            );
        }

        function getHotelsRequest(tripId: string):ng.IPromise<HotelsRequest> {
            return service.getTrip(tripId).then<HotelsRequest>(trip => trip.HotelsRequest);
        }

        function getTransfersRequest(tripId: string):ng.IPromise<TransfersRequest> {
            return service.getTrip(tripId).then<TransfersRequest>(trip => trip.TransfersRequest);
        }

        function _getTripDate(tripId: string, requestType: string, dateType: string, serialised: boolean)
            : ng.IPromise<Date|string|null> {
            return service.getTrip(tripId).then<Date|string|null>(trip =>
                trip && trip[requestType] && trip[requestType][dateType] ?
                    (serialised ? dateUtil.serialiseDate : dateUtil.parseDate)(trip[requestType][dateType])
                    : null);
        }

        function getHotelsRequestCheckInDate(tripId: string, serialised: boolean = false)
            : ng.IPromise<Date|string|null> {
            return _getTripDate(tripId, 'HotelsRequest', 'CheckInDate', serialised);
        }

        function getHotelsRequestCheckOutDate(tripId: string, serialised: boolean = false)
            : ng.IPromise<Date|string|null> {
            return _getTripDate(tripId, 'HotelsRequest', 'CheckOutDate', serialised);
        }

        function getFlightsRequestDepartureDate(tripId: string, serialised: boolean = false)
            : ng.IPromise<Date|string|null> {
            return _getTripDate(tripId, 'FlightsRequest', 'DepartureDate', serialised);
        }

        function getFlightsRequestReturnDate(tripId: string, serialised:boolean = false)
            : ng.IPromise<Date|string|null> {
            return _getTripDate(tripId, 'FlightsRequest', 'ReturnDate', serialised);
        }

        function getSelectedDepartureAirport(tripId: string): ng.IPromise<Airport|null> {
            return service.getTrip(tripId).then(trip =>
                trip && trip.FlightsRequest ? trip.FlightsRequest.SelectedDepartureAirport : null);
        }

        //TODO - this needs investigating because DepartureAirports does not exist on the FlightsRequest
        function getDepartureAirports(tripId: string): ng.IPromise<any> {
            return getTrip(tripId).then(trip =>
                trip && trip.FlightsRequest ? trip.FlightsRequest.DepartureAirports : []);
        }

        function getParkingAirport(tripId: string): ng.IPromise<Airport> {
            return service.getTrip(tripId).then(trip =>
                trip && trip.ParkingRequest ? _.omit(trip.ParkingRequest.Airport, 'ShortTitle') : {});
        }

        //TODO - this return type mayes no sense because SelectedDestinationAirport is not an array
        function getSelectedDestinationAirport(tripId: string): ng.IPromise<Airport|null> {
            return getTrip(tripId).then(trip =>
                trip && trip.FlightsRequest ? trip.FlightsRequest.SelectedDestinationAirport : []);
        }

        //TODO - this needs investigating because DestinationAirports does not exist on the FlightsRequest
        function getDestinationAirports(tripId: string): ng.IPromise<any> {
            return getTrip(tripId).then(trip =>
                trip && trip.FlightsRequest ? trip.FlightsRequest.DestinationAirports : []);
        }

        function getFlightCabin(tripId: string): ng.IPromise<number> {
            return getTrip(tripId).then(trip =>
                trip && trip.FlightsRequest ? trip.FlightsRequest.Cabin : null);
        }

        function getParkingDropOffDate(tripId: string, serialised: boolean = false): ng.IPromise<Date|string|null> {
            return _getTripDate(tripId, 'ParkingRequest', 'DepartureDate', serialised);
        }

        function getParkingPickUpDate(tripId: string, serialised: boolean = false): ng.IPromise<Date|string|null> {
            return _getTripDate(tripId, 'ParkingRequest', 'ReturnDate', serialised);
        }

        function _parseTripDates(tripId: string): ng.IPromise<any[]> {
            const dates = [
                new dateUtil.DateRange(),
                new dateUtil.DateRange(),
                new dateUtil.DateRange(),
                new dateUtil.DateRange()
            ];
            return getTrip(tripId).then(trip => {
                if (trip) {
                    if (trip.HotelsRequest) {
                        if (trip.HotelsRequest.CheckInDate) {
                            setStartDate(searchUnitService.hotel, trip.HotelsRequest.CheckInDate);
                        }
                        if (trip.HotelsRequest.CheckOutDate) {
                            setEndDate(searchUnitService.hotel, trip.HotelsRequest.CheckOutDate);
                        }
                    }
                    if (trip.FlightsRequest) {
                        if (trip.FlightsRequest.DepartureDate) {
                            setStartDate(searchUnitService.flight, trip.FlightsRequest.DepartureDate);
                        }
                        if (trip.FlightsRequest.ReturnDate) {
                            setEndDate(searchUnitService.flight, trip.FlightsRequest.ReturnDate);
                        }
                    }
                    if (trip.TransfersRequest) {
                        if (trip.TransfersRequest.ArrivalDate) {
                            setStartDate(searchUnitService.transfer, trip.TransfersRequest.ArrivalDate);
                        }
                        if (trip.TransfersRequest.ReturnDate) {
                            setEndDate(searchUnitService.transfer, trip.TransfersRequest.ReturnDate);
                        }
                    }
                    if (trip.MarhabaRequest && trip.MarhabaRequest.DateAndTime) {
                        setStartDate(searchUnitService.marhaba, trip.MarhabaRequest.DateAndTime);
                    }
                }

                return dates;
            });

            function setStartDate(mode: number, date: string) {
                dates[mode].setStart(date);
            }

            function setEndDate(mode: number, date: string) {
                dates[mode].setEnd(date);
            }
        }

        function getTripNumberOfNights(mode: number, tripId: string): ng.IPromise<number|null|void> {
            return (!tripId || null == mode)
                ? $q.when()
                : _parseTripDates(tripId)
                    .then<number|null>(dates => dates && dates[mode] && dates[mode].numberOfNights());
        }

        function getDurationSummary(mode: number, tripId: string): ng.IPromise<string> {
            if (!tripId) {
                return $q.when('');
            }
            return _parseTripDates(tripId).then(dates =>
                (mode === searchUnitService.flight)
                    ? isOneWayFlightEnabled(tripId)
                        .then(oneWay =>
                            oneWay ? dateUtil.format(dates[mode].start) : dates[mode].getDurationSummary())
                    : dates[mode].getDurationSummary());
        }

        function isOneWayFlightEnabled(tripId: string): ng.IPromise<boolean> {
            return getTrip(tripId)
                .then(trip => trip && trip.FlightsRequest && trip.FlightsRequest.OneWayOnly);
        }

        function getTripOccupancy(tripId: string): ng.IPromise<Occupancy[]|null> {
            return getTrip(tripId).then<Occupancy[]|null>(trip => {
                if (!trip) {
                    return null;
                }
                const req = trip.HotelsRequest
                    || trip.FlightsRequest
                    || trip.TransfersRequest
                    || trip.MarhabaRequest
                    || trip.InsuranceRequest;

                if (!req) {
                    if (trip.ParkingRequest) {
                        return trip.ParkingRequest.PassengerCount;
                    }
                    return null;
                }
                return req.Rooms || [_.pick(req.Occupancy || req, 'Adults', 'ChildAges')];
            });
        }

        function getTripReducedOccupancy(tripId: string): ng.IPromise<Occupancy> {
            return getTripOccupancy(tripId).then(occupancy =>
                occupancy &&
                    occupancy.map(o => o.ChildAges ? o : _.set(o, 'ChildAges', []))
                        .reduce((o1, o2) => ({
                    Adults: o1.Adults + o2.Adults,
                    ChildAges: (o1.ChildAges||[]).concat(o2.ChildAges||[])
                }))
            );
        }

        function getTransferDestinationAirport(tripId: string): ng.IPromise<Airport|null> {
            return getTrip(tripId).then(trip =>
                trip && trip.TransfersRequest ? trip.TransfersRequest.DestinationAirport : null);
        }

        function getTransfersRequestSummary(tripId: string): ng.IPromise<any> {
            function getTransferNumericDirection(tripId: string): ng.IPromise<number|null|void> {
                return getTrip(tripId).then<number|null|void>(trip => {
                    if (trip && trip.TransfersRequest && trip.TransfersRequest) {
                        const {ArrivalDate, ReturnDate} = trip.TransfersRequest;
                        if (ArrivalDate && ReturnDate) {
                            return 1;
                        }
                        if (ArrivalDate) {
                            return 2;
                        }
                        if (ReturnDate) {
                            return 3;
                        }
                        return null;
                    }
                });
            }

            function getTransferDestination(tripId: string): ng.IPromise<any> {
                return getTrip(tripId).then(trip => {
                    if (trip && trip.TransfersRequest && trip.TransfersRequest.LocationId) {
                        return destinationService.getDetails({
                            LocationId: trip.TransfersRequest.LocationId
                        });
                    }
                });
            }

            function _getTransferPickupPoint(tripId: string): ng.IPromise<string|null> {
                return getTrip(tripId).then<string|null>(trip => {
                    return _.get(trip, 'TransfersRequest.Route.PickUpPoint.Description');
                });
            }

            function _getTransferDropOffPoint(tripId: string): ng.IPromise<string|null> {
                return getTrip(tripId).then<string|null>(trip => {
                    return _.get(trip, 'TransfersRequest.Route.DropOffPoint.Description');
                });
            }

            return $q.all([
                getTransferDestinationAirport(tripId),
                _getTripDate(tripId, 'TransfersRequest', 'ArrivalDate', true),
                _getTripDate(tripId, 'TransfersRequest', 'ReturnDate', true),
                service.getTripOccupancy(tripId),
                getTransferNumericDirection(tripId),
                getTransferDestination(tripId),
                _getTransferPickupPoint(tripId),
                _getTransferDropOffPoint(tripId)
            ]).then(([
                DestinationAirport,
                StartDate,
                EndDate,
                occupancy,
                NumericDirection,
                Destination,
                PickUpPointTitle,
                DropOffPointTitle]) => {
                return {
                    StartDate,
                    EndDate,
                    OccupancyString: occupancyService.getOccupancyString(occupancy),
                    NumericDirection,
                    DestinationTitle: (Destination && Destination.ShortTitle)
                        || (DestinationAirport && DestinationAirport.Title),
                    PickUpPointTitle,
                    DropOffPointTitle,
                    TransferType: searchUnitService.getTransferType()
                };
            });
        }

        function getInsuranceRequestSummary(tripId: string): ng.IPromise<any> {
            return getTrip(tripId).then(trip => {
                const insuranceReq = (trip && trip.InsuranceRequest) || {};
                return {
                    PolicyType: insuranceReq.TripType,
                    StartDate: dateUtil.serialiseDate(insuranceReq.StartDate),
                    EndDate: dateUtil.serialiseDate(insuranceReq.EndDate),
                    Destination: insuranceReq.TravelDestination
                };
            });
        }

        function getParkingRequestSummary(tripId: string): ng.IPromise<any> {
            return getTrip(tripId)
                .then(trip => {
                    const parkingReq = (trip && trip.ParkingRequest) || {};
                    return {
                        Airport: parkingReq.Airport.ShortTitle,
                        DepartureDate: dateUtil.serialiseDate(parkingReq.DepartureDate),
                        ReturnDate: dateUtil.serialiseDate(parkingReq.ReturnDate),
                        PassengerCount: parkingReq.PassengerCount
                    };
                });
        }

        function getHotelsRequestNumberOfNights(tripId: string): ng.IPromise<number> {
            if (!tripId) {
                return $q.when(0);
            }
            return $q.all([
                service.getHotelsRequestCheckInDate(tripId, false),
                service.getHotelsRequestCheckOutDate(tripId, false)
            ]).then(dates => dateUtil.numberOfNights(...dates));
        }

        function getTripFromQuote(quote): ng.IPromise<TripFromQuoteResponse> {
            //create request object
            const tripFromQuoteRequest: TripFromQuoteRequest = {
                QuoteId: quote.Id,
                BasketId: quote.BasketId,
                ForHoliday: quote.ForHoliday
            };
            quote.Products.reduce((req, {AvailabilityId, Type}) => {
                switch (Type) {
                    case 'Accommodation':
                        req.HotelsRequestId = AvailabilityId;
                        break;
                    case 'Airport Parking':
                        req.ParkingRequestId = AvailabilityId;
                        break;
                    case 'Attraction Ticket':
                        req.AttractionsRequestId = AvailabilityId;
                        break;
                    case 'Car Hire':
                        req.CarHireRequestId = AvailabilityId;
                        break;
                    case 'Flight':
                        req.FlightsRequestId = AvailabilityId;
                        break;
                    case 'Insurance':
                        req.InsuranceRequestId = AvailabilityId;
                        break;
                    case 'Marhaba':
                        req.MarhabaRequestId = AvailabilityId;
                        break;
                    case 'Transfer':
                        req.TransfersRequestId = AvailabilityId;
                        break;
                }
                return req;
            }, tripFromQuoteRequest);

            return $http.post<TripFromQuoteResponse>(`${url}createtripfromquote`,
                tripFromQuoteRequest,
                {ignoreErrors: true}).then(res => res.data);
        }

        function usePackageRates(tripId = $routeParams && $routeParams.tripId): ng.IPromise<boolean> {
            return getTrip(tripId)
                .then(trip =>
                    trip && trip.TripType === tripTypes.Holiday && (TRPCONFIG.Domain.PackagePrice || trip.PackageId)
                )
                .then(result => !!result);
        }

        function getPackageId(tripId = $routeParams && $routeParams.tripId): ng.IPromise<string> {
            return getTrip(tripId).then(trip => trip && trip.PackageId);
        }

        function getFlightsRequest(tripId: string): ng.IPromise<FlightsRequest> {
            return service.getTrip(tripId).then(trip => trip && trip.FlightsRequest);
        }


        return service;
    }
}
